import React, { useEffect } from 'react';
import { FlexRow, Grid, Header3Text } from 'ui-toolkit';
import { Field, FieldArray, FieldProps } from 'formik';
import { BlockRedirection } from '../../../../cdBuilder/BlockRedirection';
import { useSelector } from 'react-redux';
import { IManualCheckDisbursementData } from '../../../../../types/order/escrow/IManualCheckDisbursementData';
import { CreateEditManualCheckDisbursementRow } from './CreateEditManualCheckDisbursementRow';
import {
  deleteManualCheckAction,
  deleteManualCheckSlice,
} from '../../../../../store/order/escrow/disbursements/manualCheckDisbursements/deleteManualCheckSlice';
import { UUID } from '../../../../../types/shared/UUID';
import { HasPermissionButton } from '../../shared/HasPermissionButton';
import { downloadEscrowCheckAndSetDate } from '../../shared/downloadEscrowCheck';
import { deleteInternalTransferSlice } from '../../../../../store/order/escrow/disbursements/internalTransfers/deleteInternalTransferSlice';
import { RootStore } from '../../../../../store/types';
import { EscrowBaseAPI } from 'API/order/escrow/EscrowBaseAPI';
import { eAPITags } from 'API/eAPITags';
import { useAppDispatch } from 'store/store';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';

export interface ICreateEditManualCheckDisbursementContainerProps {
  orderId: UUID;
  dirty: boolean;
  onTransactionUpdate: () => void;
}

export const CreateEditManualCheckDisbursementContainer: React.FC<ICreateEditManualCheckDisbursementContainerProps> = ({
  orderId,
  dirty,
  onTransactionUpdate,
}) => {
  const dispatch = useAppDispatch();
  const hasEditPermission = useHasPermissions([ePermissions.EscrowManualCheckDisbursementsEdit]);

  const {
    deleteManualCheck: { fetchStatus },
  } = useSelector((store: RootStore) => store.order.escrow.disbursements);

  useEffect(() => {
    if (fetchStatus.success) {
      onTransactionUpdate();
      dispatch(deleteManualCheckSlice.actions.reset({}));
    }
  }, [dispatch, onTransactionUpdate, fetchStatus]);

  const onRemoveManualCheck = async (checkToRemove: IManualCheckDisbursementData) => {
    if (checkToRemove.createdAt) {
      await dispatch(deleteManualCheckAction(checkToRemove.id));
      onTransactionUpdate();
    } else {
      dispatch(deleteInternalTransferSlice.actions.setData({}));
    }
  };

  const onCreatePDF = async (checkId: string) => {
    downloadEscrowCheckAndSetDate()(checkId);
    dispatch(EscrowBaseAPI.util.invalidateTags([{ type: eAPITags.DetailedEscrowData, id: orderId }]));
  };

  return (
    <>
      <BlockRedirection showSaveSpinner={false} showSaveSuccess={false} isDirty={dirty} />
      <FieldArray name={'manualCheckDisbursements'}>
        {({ push, remove }) => (
          <>
            <Grid>
              <FlexRow>
                <Header3Text>Manual Checks</Header3Text>
                <HasPermissionButton
                  hasEditPermission={hasEditPermission}
                  push={push}
                  successMessage={'Add Check'}
                  transferType={'manual checks'}
                />
              </FlexRow>
              <Field name={'manualCheckDisbursements'}>
                {({ field }: FieldProps<IManualCheckDisbursementData[]>) => (
                  <>
                    {field.value.map((check, index) => (
                      <CreateEditManualCheckDisbursementRow
                        key={check?.id || `newCheck-${index}`}
                        index={index}
                        onDelete={async (index) => {
                          remove(index);
                          await onRemoveManualCheck(check);
                        }}
                        onCreatePDF={onCreatePDF}
                        fieldName={'manualCheckDisbursements'}
                        check={check}
                      />
                    ))}
                  </>
                )}
              </Field>
            </Grid>
          </>
        )}
      </FieldArray>
    </>
  );
};
